import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, CalendarDays, ListChecks, Sparkles, UtensilsCrossed, ShoppingCart, Gift, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MemberAvatar } from "@/components/MemberAvatar";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/chores", label: "Chores", icon: ListChecks },
  { to: "/cleaning", label: "Cleaning", icon: Sparkles },
  { to: "/meals", label: "Meals", icon: UtensilsCrossed },
  { to: "/shopping", label: "Shopping", icon: ShoppingCart },
  { to: "/rewards", label: "Rewards", icon: Gift },
];

function Logo() {
  return (
    <div className="flex items-center gap-2.5">
      <img src="/logo.png" alt="BentoLife" className="h-9 w-9 rounded-xl border border-border" />
      <span className="font-display text-xl font-700 font-semibold text-ink">BentoLife</span>
    </div>
  );
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 rounded-full p-1 pr-3 hover:bg-secondary transition-colors"
          data-testid="user-menu-trigger"
        >
          <MemberAvatar member={{ name: user?.name, color: "sage", avatar_url: user?.picture }} size="sm" />
          <span className="hidden text-sm font-semibold text-ink sm:block max-w-[120px] truncate">
            {user?.name}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="logout-btn"
          onClick={async () => {
            await signOut();
            navigate("/landing");
          }}
        >
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Layout() {
  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 z-30 hidden h-screen w-64 flex-col border-r border-border bg-card px-4 py-6 lg:flex">
        <div className="px-2">
          <Logo />
        </div>
        <nav className="mt-8 flex flex-1 flex-col gap-1.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition-colors",
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-ink"
                )
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Top bar */}
      <header className="glass sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border px-4 lg:pl-[17rem] lg:pr-8">
        <div className="lg:hidden">
          <Logo />
        </div>
        <div className="hidden lg:block" />
        <UserMenu />
      </header>

      {/* Main content */}
      <main className="mx-auto w-full max-w-6xl px-4 pb-28 pt-6 lg:pl-[17rem] lg:pr-8 lg:pb-10">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="glass fixed bottom-0 left-0 z-30 flex w-full items-stretch justify-around border-t border-border lg:hidden">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`bottomnav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              cn(
                "flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold transition-colors",
                isActive ? "text-primary" : "text-muted-foreground"
              )
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
